import { BaseComponent } from './BaseComponent';
import { Announcement } from '../types';
import { Config } from '../core/Config';
import { sanitizeHtml, sanitizeUrl } from '../utils/sanitize';

export class Popup extends BaseComponent {
    private announcement: Announcement;
    private onClose: () => void;
    private onCtaClick: () => void;
    private showBranding: boolean;
    private onBack?: () => void;

    constructor(
        config: Config,
        announcement: Announcement,
        onClose: () => void,
        onCtaClick: () => void,
        showBranding: boolean = true,
        onBack?: () => void
    ) {
        super(config);
        this.announcement = announcement;
        this.onClose = onClose;
        this.onCtaClick = onCtaClick;
        this.showBranding = showBranding;
        this.onBack = onBack;
    }

    protected lockScroll(): boolean {
        return true;
    }

    protected closeOnEscape(): boolean {
        return true;
    }

    protected onEscape(): void {
        this.handleClose();
    }

    protected render(): HTMLElement {
        const overlay = this.createElement('div', 'pr-overlay pr-sdk');
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-labelledby', 'pr-popup-title');

        const popup = this.createElement('div', 'pr-popup');

        // Back affordance — only when this popup was opened from the list, so
        // the user can return to it. Boost-triggered popups pass no callback and
        // render exactly as before.
        if (this.onBack) {
            popup.classList.add('pr-popup--with-back');

            const backBtn = this.createElement('button', 'pr-popup-back');
            backBtn.type = 'button';
            backBtn.textContent = `← ${this.config.t.back}`;
            backBtn.setAttribute('aria-label', this.config.t.back);
            backBtn.onclick = () => this.handleBack();
            popup.appendChild(backBtn);
        }

        // Close button
        const closeBtn = this.createElement('button', 'pr-popup-close');
        closeBtn.innerHTML = '&times;';
        closeBtn.setAttribute('aria-label', this.config.t.close);
        closeBtn.onclick = () => this.handleClose();
        popup.appendChild(closeBtn);

        // Content
        const content = this.createElement('div', 'pr-popup-content');

        const title = this.createElement('h2', 'pr-popup-title');
        title.id = 'pr-popup-title';
        title.textContent = this.announcement.title;
        content.appendChild(title);

        // Image (if exists) - positioned between title and description
        if (this.announcement.image_url) {
            // Run the image URL through the same scheme guard as CTA links so a
            // javascript:/data: URL that slipped past the server can't ride in
            // on the src.
            const safeSrc = sanitizeUrl(this.announcement.image_url);
            if (safeSrc) {
                const img = this.createElement('img', 'pr-popup-image');
                img.src = safeSrc;
                img.alt = this.announcement.title;
                content.appendChild(img);
            }
        }

        const description = this.createElement('div', 'pr-popup-description');
        description.innerHTML = sanitizeHtml(this.announcement.description);
        content.appendChild(description);

        // CTA button (if exists)
        if (this.announcement.action_button_text && this.announcement.action_button_url) {
            const ctaBtn = this.createElement('button', 'pr-popup-cta');
            ctaBtn.textContent = this.announcement.action_button_text;
            ctaBtn.onclick = () => this.handleCtaClick();

            // Apply custom button color if specified
            if (this.announcement.action_button_color) {
                ctaBtn.style.backgroundColor = this.announcement.action_button_color;
                ctaBtn.style.borderColor = this.announcement.action_button_color;
            }

            content.appendChild(ctaBtn);
        }

        popup.appendChild(content);

        // Footer with close button and optional branding
        const footer = this.createElement('div', 'pr-popup-footer');

        // Close button
        const closeButton = this.createElement('button', 'pr-popup-close-btn');
        closeButton.textContent = this.config.t.close;
        closeButton.onclick = () => this.handleClose();
        footer.appendChild(closeButton);

        // Add "Powered by" branding (if enabled)
        if (this.showBranding) {
            const poweredBy = this.createElement('a', 'pr-powered-by');
            poweredBy.textContent = 'Powered by PulseRelease.com';
            poweredBy.href = 'https://pulserelease.com';
            poweredBy.target = '_blank';
            poweredBy.rel = 'noopener noreferrer';
            footer.appendChild(poweredBy);
        }

        popup.appendChild(footer);

        overlay.appendChild(popup);

        // Close on overlay click
        overlay.onclick = (e) => {
            if (e.target === overlay) {
                this.handleClose();
            }
        };

        return overlay;
    }

    private handleClose(): void {
        this.hide();
        this.onClose();
    }

    // Back does NOT hide the popup itself: the orchestrator owns the hand-off
    // and shows the list before hiding this popup, so the shared scroll lock is
    // never released in between (no host-page jump). Close/Escape keep meaning
    // "close everything".
    private handleBack(): void {
        this.onBack?.();
    }

    private handleCtaClick(): void {
        this.onCtaClick();

        // Never hand a javascript:/data: URL to window.open, even if one slipped
        // past server-side sanitization.
        const safeUrl = sanitizeUrl(this.announcement.action_button_url);
        if (safeUrl) {
            window.open(safeUrl, '_blank', 'noopener,noreferrer');
        }

        this.handleClose();
    }
}
